import React, { useEffect, useState } from 'react';
import { Box } from '@abyss/web/ui/Box';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Label } from '@abyss/web/ui/Label';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { BreadcrumbItem } from '@src/api/common/BreadcrumbTypes';
import { PageTitle } from '@src/api/common/PageTitleType';
import { CookieService } from '@src/api/common/CookieService';
import { API_URL } from '@src/constants/BaseUrls';

const UPLOAD_PROCESSING_KEY = 'routeToolUploadProcessing';

const DashboardTitle: PageTitle = { title: '' };
const DashboardBreadcrumbs: Array<BreadcrumbItem> = [
  { title: 'Home', isLink: true, href: '/' },
  { title: 'Route Tool', isLink: false },
  { title: 'Dashboard', isLink: false },
];

const normalizeBaseUrl = (url?: string) =>
  url ? (url.endsWith('/') ? url.slice(0, -1) : url) : '';

const resolveDashboardApiBase = () => {
  const configured = normalizeBaseUrl(API_URL);
  if (!configured || configured.includes('xyz-dev.com')) return 'http://localhost:5000';
  return configured;
};

type RecentUpload = { user: string; entity: string; timestamp: string };

type DashboardData = {
  total_uploads: number;
  uploads_by_entity: Record<string, number>;
  uploads_by_user: Record<string, number>;
  uploads_by_action: Record<string, number>;
  recent_uploads: RecentUpload[];
};

const StatCard = ({
  icon,
  label,
  value,
  color = '#196ecf',
}: {
  icon: string;
  label: string;
  value: string | number;
  color?: string;
}) => (
  <div
    style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '12px 14px',
      height: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    }}
  >
    <div
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        backgroundColor: `${color}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <IconSymbol icon={icon as any} size={20} color={color as any} variant="filled" />
    </div>
    <div style={{ minWidth: 0 }}>
      <span style={{ fontSize: '11px', color: '#6b7a8d', fontWeight: 500, display: 'block', marginBottom: '2px' }}>{label}</span>
      <div style={{ fontSize: '20px', fontWeight: 800, color, lineHeight: 1 }}>
        {value}
      </div>
    </div>
  </div>
);

const BreakdownBar = ({
  entries,
  color = '#196ecf',
}: {
  entries: [string, number][];
  color?: string;
}) => {
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {entries.map(([key, val]) => (
        <div key={key}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', color: '#3d4f60', fontWeight: 600 }}>{key}</span>
            <span style={{ fontSize: '13px', color, fontWeight: 700 }}>{val}</span>
          </div>
          <div
            style={{
              width: '100%',
              height: '7px',
              backgroundColor: '#e8edf4',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${(val / max) * 100}%`,
                backgroundColor: color,
                borderRadius: '4px',
                transition: 'width 0.6s ease',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const ACTION_LABEL_MAP: Record<string, string> = {
  'File Modified': 'Files Modified',
  'File Uploaded': 'Files Uploaded',
  'file modified': 'Files Modified',
  'file uploaded': 'Files Uploaded',
};

const normalizeActionLabel = (label: string): string =>
  ACTION_LABEL_MAP[label] ?? label;

const ActionBadges = ({ entries }: { entries: [string, number][] }) => {
  const colors = ['#196ecf', '#0d7a55', '#a05c00', '#7c3aed'];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
      {entries.map(([action, count], i) => {
        const fg = colors[i % colors.length];
        return (
          <div
            key={action}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor: `${fg}12`,
              border: `1px solid ${fg}28`,
            }}
          >
            <span style={{ fontSize: '12px', color: fg, fontWeight: 600, lineHeight: 1.3 }}>{normalizeActionLabel(action)}</span>
            <span style={{ fontSize: '15px', fontWeight: 800, color: fg, lineHeight: 1, flexShrink: 0 }}>{count}</span>
          </div>
        );
      })}
    </div>
  );
};

const SectionCard = ({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: React.ReactNode;
}) => (
  <div
    style={{
      height: '100%',
      backgroundColor: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
    }}
  >
    <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexShrink: 0 }}>
        <IconSymbol icon={icon as any} size={16} color="$primary1" variant="filled" />
        <span style={{ fontSize: '13px', fontWeight: 700, color: '#002677', letterSpacing: '0.2px' }}>{title}</span>
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  </div>
);

const Skeleton = ({ height = 16, width = '100%' }: { height?: number; width?: string }) => (
  <div
    style={{
      height,
      width,
      borderRadius: '6px',
      backgroundColor: '#e8edf4',
      animation: 'dashSkeletonPulse 1.4s ease-in-out infinite',
    }}
  />
);

export const DashboardScreen = () => {
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();
  const baseUrl = resolveDashboardApiBase();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [uploadProcessing, setUploadProcessing] = useState<{ entity: string; startedAt: number } | null>(null);
  const [uploadProcessingMsg, setUploadProcessingMsg] = useState('');
  const [uploadBarProgress, setUploadBarProgress] = useState(5);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [completedEntity, setCompletedEntity] = useState('');

  useEffect(() => {
    (async () => {
      await UpdatePageTitle(DashboardTitle);
      await UpdateBreadcrumb(DashboardBreadcrumbs);
    })();
  }, []);

  // Read sessionStorage on mount for in-progress upload
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(UPLOAD_PROCESSING_KEY);
      if (raw) {
        const state = JSON.parse(raw) as { entity: string; startedAt: number };
        if (state?.entity) {
          setUploadProcessing(state);
          setUploadProcessingMsg('Processing uploaded files…');
          setUploadCompleted(false);
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Listen for upload-started custom event (SPA navigation)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ entity: string; startedAt: number }>).detail;
      if (detail?.entity) {
        setUploadProcessing({ entity: detail.entity, startedAt: detail.startedAt });
        setUploadProcessingMsg('Processing uploaded files…');
        setUploadBarProgress(5);
        setUploadCompleted(false);
      }
    };
    window.addEventListener('routeToolUploadStarted', handler);
    return () => window.removeEventListener('routeToolUploadStarted', handler);
  }, []);

  // Fake progress animation
  useEffect(() => {
    if (!uploadProcessing) return undefined;
    setUploadBarProgress(5);
    const interval = setInterval(() => {
      setUploadBarProgress((prev) => {
        const next = prev + Math.random() * 6 + 2;
        return next >= 90 ? 90 : next;
      });
    }, 700);
    return () => clearInterval(interval);
  }, [uploadProcessing]);

  // Poll backend status
  useEffect(() => {
    if (!uploadProcessing) return undefined;
    let cancelled = false;
    const { entity, startedAt } = uploadProcessing;
    const statusUrl = `${baseUrl}/api/entities/${encodeURIComponent(entity)}/status`;
    const poll = async () => {
      while (!cancelled) {
        try {
          const token = CookieService.GetCookie('authToken');
          const hdrs: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
          // eslint-disable-next-line no-await-in-loop
          const res = await fetch(statusUrl, { headers: hdrs });
          if (res.ok) {
            // eslint-disable-next-line no-await-in-loop
            const body = await res.json().catch(() => ({}));
            const blobs = body?.blobs ?? {};
            const allComplete =
              blobs.all_complete === true ||
              (blobs.site_clusters === true && blobs.site_travel === true &&
               blobs.user_sites === true && blobs.warehouse_clusters === true);
            const stage: string = body?.job?.stage || body?.stage || '';
            const elapsed = Math.round((Date.now() - startedAt) / 1000);
            setUploadProcessingMsg(`Processing${stage ? ` — ${stage}` : ''} (${elapsed}s)…`);
            if (allComplete) {
              setUploadBarProgress(100);
              setTimeout(() => {
                try { window.sessionStorage.removeItem(UPLOAD_PROCESSING_KEY); } catch { /* */ }
                setCompletedEntity(entity);
                setUploadProcessing(null);
                setUploadProcessingMsg('');
                setUploadCompleted(true);
              }, 1200);
              return;
            }
          }
        } catch { /* keep polling */ }
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => { setTimeout(resolve, 3000); });
      }
    };
    void poll();
    return () => { cancelled = true; };
  }, [uploadProcessing]);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const token = CookieService.GetCookie('authToken');
        const headers: Record<string, string> = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        const response = await fetch(`${baseUrl}/api/dashboard/uploads`, { headers });
        if (!response.ok) {
          setError(`Failed to load dashboard data (${response.status})`);
          return;
        }
        const json = (await response.json()) as DashboardData;
        setData(json);
      } catch {
        setError('Unable to reach the dashboard API. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };
    void fetchDashboard();
  }, [baseUrl]);

  const entityEntries = Object.entries(data?.uploads_by_entity ?? {});
  const userEntries = Object.entries(data?.uploads_by_user ?? {});
  const actionEntries = Object.entries(data?.uploads_by_action ?? {});
  const recentUploads = data?.recent_uploads ?? [];

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      <style>{`
        @keyframes dashSkeletonPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.45; }
        }
        .dash-stat-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
          align-items: stretch;
        }
        .dash-two-col-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 20px;
          align-items: stretch;
        }
        @media (max-width: 1100px) {
          .dash-stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 860px) {
          .dash-two-col-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 600px) {
          .dash-stat-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: '14px 18px',
            borderRadius: '8px',
            backgroundColor: '#fff4f4',
            border: '1px solid #fbb',
            color: '#a30000',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <IconSymbol icon="error" size={20} color="$error1" />
          {error}
        </div>
      )}

      {/* Monitor Upload — always at top */}
      <div
        style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          padding: '14px 16px',
        }}
      >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: uploadProcessing || uploadCompleted ? '12px' : '0' }}>
              <IconSymbol icon="monitoring" size={16} color="$primary1" variant="filled" />
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#002677', letterSpacing: '0.2px' }}>Monitor Upload</span>
              {uploadProcessing && (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#196ecf', animation: 'monitorPulse 1.4s ease-in-out infinite' }} />
                  <span style={{ fontSize: '12px', color: '#196ecf', fontWeight: 600 }}>Processing</span>
                </div>
              )}
            </div>
            {uploadProcessing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IconSymbol icon="corporate_fare" size={16} color="$primary1" />
                    <span style={{ fontWeight: 700, fontSize: '14px', color: '#1a2d4a' }}>{uploadProcessing.entity}</span>
                  </div>
                  <span style={{ fontSize: '12px', color: '#6b7a8d' }}>{uploadProcessingMsg}</span>
                </div>
                <div>
                  <div style={{ width: '100%', height: '7px', backgroundColor: '#e8edf4', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${uploadBarProgress}%`, backgroundColor: '#196ecf', borderRadius: '4px', transition: 'width 0.5s ease' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                    <Label size="sm" color="$gray6">Backend is processing your uploaded files</Label>
                    <span style={{ fontSize: '12px', color: '#196ecf', fontWeight: 700 }}>{Math.round(uploadBarProgress)}%</span>
                  </div>
                </div>
              </div>
            ) : uploadCompleted ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0' }}>
                <IconSymbol icon="check_circle" size={22} color="$success2" variant="filled" />
                <div>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#1a2d4a' }}>Upload processing complete</span>
                  {completedEntity && (
                    <span style={{ fontSize: '14px', color: '#196ecf', fontWeight: 600 }}> — {completedEntity}</span>
                  )}
                  <div style={{ marginTop: '8px' }}>
                    <button
                      onClick={() => navigate(`/route-tool/results/${encodeURIComponent(completedEntity)}`)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '5px 14px',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#196ecf',
                        backgroundColor: '#e8f2fc',
                        border: '1px solid #196ecf',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        textDecoration: 'none',
                      }}
                    >
                      <IconSymbol icon="open_in_new" size={13} color="$primary1" />
                      Go to Results{completedEntity ? ` — ${completedEntity}` : ''}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0' }}>
                <IconSymbol icon="info" size={20} color="$gray5" variant="outlined" />
                <span style={{ fontSize: '14px', color: '#6b7a8d' }}>No upload processing going on.</span>
              </div>
            )}
          <style>{`
            @keyframes monitorPulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50%       { opacity: 0.4; transform: scale(0.8); }
            }
          `}</style>
        </div>

      {/* Uploads by Action + Recent Uploads — side by side */}
      <div className="dash-two-col-grid">
        <SectionCard icon="fact_check" title="Uploads by Action">
          {/* Stat mini-row inside the tile */}
          <div className="dash-stat-grid" style={{ marginBottom: '16px' }}>
            {loading ? (
              <><Skeleton height={48} /><Skeleton height={48} /><Skeleton height={48} /></>
            ) : (
              <>
                <StatCard icon="cloud_upload" label="Total Uploads" value={data?.total_uploads ?? 0} color="#196ecf" />
                <StatCard icon="corporate_fare" label="Entities" value={entityEntries.length} color="#0d7a55" />
                <StatCard icon="group" label="Active Users" value={userEntries.length} color="#7c3aed" />
              </>
            )}
          </div>
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '14px' }}>
            {loading ? (
              <div style={{ display: 'flex', gap: '12px' }}>
                <Skeleton height={44} width="140px" /><Skeleton height={44} width="140px" />
              </div>
            ) : actionEntries.length > 0 ? (
              <ActionBadges entries={actionEntries} />
            ) : (
              <Label size="sm" color="$gray5">No data available.</Label>
            )}
          </div>
        </SectionCard>

        <SectionCard icon="history" title="Recent Uploads">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[1, 2, 3].map((i) => <Skeleton key={i} height={36} />)}
            </div>
          ) : recentUploads.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f4f7fb' }}>
                    {['User', 'Entity', 'Timestamp'].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: '10px 14px',
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#5a6a7e',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          borderBottom: '1px solid #e2e8f0',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentUploads.slice(0, 5).map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f4f8' }}>
                      <td style={{ padding: '10px 14px', fontSize: '13px', color: '#1a2d4a', fontWeight: 600 }}>
                        {row.user || '—'}
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: '13px', color: '#3d4f60' }}>
                        {row.entity || '—'}
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6b7a8d', whiteSpace: 'nowrap' }}>
                        {row.timestamp ? new Date(row.timestamp).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Label size="sm" color="$gray5">No recent uploads found.</Label>
          )}
        </SectionCard>
      </div>

      {/* Uploads by Entity + Uploads by User — side by side */}
      <div className="dash-two-col-grid">
        <SectionCard icon="corporate_fare" title="Uploads by Entity">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Skeleton height={12} /><Skeleton height={12} width="80%" /><Skeleton height={12} width="60%" />
            </div>
          ) : entityEntries.length > 0 ? (
            <BreakdownBar entries={entityEntries} color="#196ecf" />
          ) : (
            <Label size="sm" color="$gray5">No data available.</Label>
          )}
        </SectionCard>
        <SectionCard icon="group" title="Uploads by User">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Skeleton height={12} /><Skeleton height={12} width="70%" /><Skeleton height={12} width="50%" />
            </div>
          ) : userEntries.length > 0 ? (
            <BreakdownBar entries={userEntries} color="#7c3aed" />
          ) : (
            <Label size="sm" color="$gray5">No data available.</Label>
          )}
        </SectionCard>
      </div>
    </Box>
  );
};
